import React, { useState, useEffect, useCallback, useRef } from 'react';
import { BookOpen, Plus, Edit2, Trash2, Save, X, AlertTriangle, Eye, EyeOff, Power, HelpCircle, ChevronDown, ChevronRight, MessageCircle, Bold, Italic, RotateCcw } from 'lucide-react';
import { neonQuery } from '../services/api';
import { getCurrentUser } from '../services/auth';

const DEFAULT_INTRO_HTML = `<p>Hi! I'm Jimmy. Ask me about our crab, shipping, or the VIP Loyalty Club.</p><p>For anything tied to a specific order, tap <strong>Message Us</strong> and our team will email you back.</p>`;

// Minimal HTML allowlist. Admins are trusted, but we still strip anything
// outside a safe subset before saving so a stray paste can't inject styles/scripts.
function sanitizeIntroHtml(html) {
  const ALLOWED = new Set(['P', 'BR', 'STRONG', 'B', 'EM', 'I']);
  const doc = new DOMParser().parseFromString(`<div>${html || ''}</div>`, 'text/html');
  const root = doc.body.firstChild;
  const walk = (node) => {
    const children = Array.from(node.childNodes);
    for (const child of children) {
      if (child.nodeType === 1) { // element
        if (!ALLOWED.has(child.tagName)) {
          // Replace disallowed element with its children
          while (child.firstChild) node.insertBefore(child.firstChild, child);
          node.removeChild(child);
        } else {
          while (child.attributes.length) child.removeAttribute(child.attributes[0].name);
          walk(child);
        }
      } else if (child.nodeType === 8) { // comment
        node.removeChild(child);
      }
    }
  };
  walk(root);
  return root.innerHTML.trim();
}

const DEFAULT_CATEGORIES = [
  'Voice & Tone',
  'Products',
  'Shipping & Delivery',
  'VIP Loyalty Club',
  'Gift Cards',
  'Returns & Guarantees',
  'Account Help',
  'FAQ',
];

const INPUT = { padding: '7px 12px', border: '1px solid var(--gray-300)', borderRadius: 6, fontSize: 12.5, fontFamily: 'inherit', outline: 'none', width: '100%', boxSizing: 'border-box' };
const TEXTAREA = { ...INPUT, minHeight: 220, resize: 'vertical', fontSize: 13, lineHeight: 1.5 };
const BTN = (bg, disabled) => ({
  display: 'inline-flex', alignItems: 'center', gap: 4, padding: '7px 14px',
  background: disabled ? 'var(--gray-300)' : bg, color: '#fff', border: 'none', borderRadius: 6,
  fontSize: 12, fontWeight: 600, cursor: disabled ? 'default' : 'pointer', fontFamily: 'inherit',
});
const emptyForm = () => ({ id: null, category: DEFAULT_CATEGORIES[0], title: '', content: '', is_active: true, sort_order: 0 });

export default function JimmyKnowledge() {
  const user = getCurrentUser();
  const userRoles = Array.isArray(user?.roles) ? user.roles : (user?.roles || '').split(',').map(r => r.trim());
  const isAllowed = userRoles.includes('Admin') || userRoles.includes('Manager');

  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // form object or null
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [filterCategory, setFilterCategory] = useState('All');
  const [showInactive, setShowInactive] = useState(false);
  const [customCategory, setCustomCategory] = useState('');
  const [showHelp, setShowHelp] = useState(() => {
    try { return localStorage.getItem('jk_help_collapsed') !== '1'; }
    catch { return true; }
  });
  const toggleHelp = () => {
    setShowHelp(prev => {
      const next = !prev;
      try { localStorage.setItem('jk_help_collapsed', next ? '0' : '1'); } catch {}
      return next;
    });
  };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const rows = await neonQuery('jimmy_knowledge_list', {});
      setEntries(rows || []);
    } catch (e) { setError(e.message); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { if (isAllowed) load(); }, [isAllowed, load]);

  // Greeting (intro_html) editor
  const editorRef = useRef(null);
  const [greeting, setGreeting] = useState('');
  const [greetingSaving, setGreetingSaving] = useState(false);
  const [greetingSavedAt, setGreetingSavedAt] = useState(null);
  const [greetingDirty, setGreetingDirty] = useState(false);
  const [greetingUpdatedBy, setGreetingUpdatedBy] = useState('');
  const [greetingUpdatedAt, setGreetingUpdatedAt] = useState('');

  const loadGreeting = useCallback(async () => {
    try {
      const rows = await neonQuery('jimmy_settings_get', {});
      const row = (rows || []).find(r => r.key === 'intro_html');
      const html = row?.value || DEFAULT_INTRO_HTML;
      setGreeting(html);
      setGreetingUpdatedBy(row?.updated_by_name || '');
      setGreetingUpdatedAt(row?.updated_at || '');
      if (editorRef.current) editorRef.current.innerHTML = html;
      setGreetingDirty(false);
    } catch (e) { /* first run may 404 — fine, stay with default */ }
  }, []);

  useEffect(() => { if (isAllowed) loadGreeting(); }, [isAllowed, loadGreeting]);

  const onEditorInput = () => {
    if (!editorRef.current) return;
    setGreeting(editorRef.current.innerHTML);
    setGreetingDirty(true);
  };

  const exec = (cmd) => { document.execCommand(cmd); editorRef.current?.focus(); onEditorInput(); };

  const resetGreeting = () => {
    if (!window.confirm('Reset the greeting to the default?')) return;
    if (editorRef.current) editorRef.current.innerHTML = DEFAULT_INTRO_HTML;
    setGreeting(DEFAULT_INTRO_HTML);
    setGreetingDirty(true);
  };

  const saveGreeting = async () => {
    const clean = sanitizeIntroHtml(greeting);
    setGreetingSaving(true);
    try {
      await neonQuery('jimmy_settings_set', {
        key: 'intro_html',
        value: clean,
        updated_by: user?.id || null,
        updated_by_name: user?.staffName || null,
      });
      setGreetingSavedAt(Date.now());
      setGreetingDirty(false);
      setGreeting(clean);
      if (editorRef.current) editorRef.current.innerHTML = clean;
    } catch (e) { setError(e.message); }
    finally { setGreetingSaving(false); }
  };

  if (!isAllowed) {
    return (
      <div style={{ padding: 40, textAlign: 'center', color: 'var(--gray-500)' }}>
        <AlertTriangle size={48} style={{ marginBottom: 12, color: 'var(--orange)' }} />
        <h2 style={{ color: 'var(--navy)' }}>Access Restricted</h2>
        <p>Jimmy Knowledge is only available to Managers and Admins.</p>
      </div>
    );
  }

  const categoriesInUse = Array.from(new Set([...DEFAULT_CATEGORIES, ...entries.map(e => e.category)])).sort();
  const visible = entries.filter(e =>
    (filterCategory === 'All' || e.category === filterCategory) &&
    (showInactive || e.is_active)
  );

  const startNew = () => { setEditing(emptyForm()); setCustomCategory(''); setError(''); };
  const startEdit = (entry) => {
    setEditing({ ...entry });
    setCustomCategory(DEFAULT_CATEGORIES.includes(entry.category) ? '' : entry.category);
    setError('');
    setTimeout(() => document.getElementById('jk-editor')?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 50);
  };
  const cancel = () => { setEditing(null); setCustomCategory(''); setError(''); };

  const save = async () => {
    const category = customCategory.trim() || editing.category;
    if (!category || !editing.title.trim() || !editing.content.trim()) {
      setError('Category, title, and content are required.');
      return;
    }
    setSaving(true);
    try {
      await neonQuery('jimmy_knowledge_upsert', {
        id: editing.id || null,
        category,
        title: editing.title.trim(),
        content: editing.content,
        is_active: !!editing.is_active,
        sort_order: Number(editing.sort_order) || 0,
        updated_by: user?.id || null,
        updated_by_name: user?.staffName || null,
      });
      setEditing(null);
      setCustomCategory('');
      await load();
    } catch (e) { setError(e.message); }
    finally { setSaving(false); }
  };

  const toggleActive = async (entry) => {
    try {
      await neonQuery('jimmy_knowledge_upsert', {
        id: entry.id,
        category: entry.category,
        title: entry.title,
        content: entry.content,
        is_active: !entry.is_active,
        sort_order: entry.sort_order,
        updated_by: user?.id || null,
        updated_by_name: user?.staffName || null,
      });
      await load();
    } catch (e) { setError(e.message); }
  };

  const remove = async (entry) => {
    if (!window.confirm(`Delete "${entry.title}"? This cannot be undone.`)) return;
    try {
      await neonQuery('jimmy_knowledge_delete', { id: entry.id });
      await load();
    } catch (e) { setError(e.message); }
  };

  const fmtDate = (d) => {
    if (!d) return '';
    try { return new Date(d).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }); }
    catch { return d; }
  };

  return (
    <div style={{ padding: 24, maxWidth: 1100 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 8 }}>
        <BookOpen size={24} color="var(--navy)" />
        <h1 style={{ fontSize: 20, fontWeight: 700, color: 'var(--navy)', margin: 0 }}>Jimmy Knowledge</h1>
      </div>
      <p style={{ color: 'var(--gray-600)', fontSize: 13, marginTop: 0, marginBottom: 20, maxWidth: 820 }}>
        What Jimmy knows when answering shoppers on crabplace.com. Active entries are bundled into Jimmy's system prompt on every chat. Keep entries short and factual — Jimmy will phrase things in his own voice.
      </p>

      {error && (
        <div style={{ background: '#fef2f2', border: '1px solid #fecaca', color: '#991b1b', padding: '10px 14px', borderRadius: 6, fontSize: 13, marginBottom: 16 }}>
          {error}
        </div>
      )}

      <div className="card" style={{ marginBottom: 20, borderColor: 'var(--navy)' }}>
        <button
          onClick={toggleHelp}
          style={{
            width: '100%', display: 'flex', alignItems: 'center', gap: 8,
            padding: '12px 16px', background: 'var(--navy)', color: '#fff',
            border: 'none', borderRadius: showHelp ? '8px 8px 0 0' : 8,
            fontSize: 13, fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit', textAlign: 'left',
          }}
        >
          {showHelp ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
          <HelpCircle size={16} />
          How This Page Works
          <span style={{ marginLeft: 'auto', fontSize: 11, fontWeight: 400, opacity: 0.8 }}>
            {showHelp ? 'click to hide' : 'click to expand'}
          </span>
        </button>
        {showHelp && (
          <div style={{ padding: '16px 20px', fontSize: 13, lineHeight: 1.55, color: 'var(--gray-800)' }}>
            <p style={{ marginTop: 0 }}>
              <strong>What this page is for.</strong> Jimmy is the chat assistant on crabplace.com (the little crab in the bottom-right corner). Every entry you add here becomes part of what Jimmy knows when answering shoppers. Think of it as writing a cheat-sheet Jimmy reads before every conversation.
            </p>

            <p>
              <strong>How to add a fact.</strong> Click <em>New Entry</em>, pick a <em>Category</em> (or type a custom one), give it a short <em>Title</em>, and paste the content. Keep each entry focused on one topic — one entry per policy, product, or common question works best. Shorter beats longer: Jimmy will rephrase things in his own voice, so you just need the facts.
            </p>

            <p>
              <strong>How to edit or retire a fact.</strong> Click the pencil icon to edit. Click the power icon to mark an entry <em>Inactive</em> — it stays saved but Jimmy ignores it until you turn it back on (good for seasonal info or things under review). Click the trash icon to delete permanently.
            </p>

            <p>
              <strong>When Jimmy sees your changes.</strong> Saves take effect on new conversations within about a minute. Jimmy is already talking to someone? Their existing chat won't pick up the update mid-conversation, but their next message will.
            </p>

            <p>
              <strong>What makes a good entry.</strong>
              <br />&nbsp;&nbsp;• Be specific. "Free shipping on orders over $100" beats "free shipping on big orders."
              <br />&nbsp;&nbsp;• Be correct. Jimmy trusts this text over its own training — wrong info here will produce wrong answers.
              <br />&nbsp;&nbsp;• Be current. If a promo ends, flip it to Inactive or delete it.
              <br />&nbsp;&nbsp;• Skip the fluff. No headings, no "as mentioned above" — Jimmy wraps the content in prose for you.
            </p>

            <p>
              <strong>What NOT to put here.</strong> Do not paste customer PII, order numbers, passwords, or anything private. This text may appear in Jimmy's answers to strangers. Policies, product specs, shipping rules, FAQs — yes. Specific customer info — never.
            </p>

            <p style={{ marginBottom: 0 }}>
              <strong>When in doubt,</strong> steer Jimmy to hand off. The "FAQ → When to Hand Off to a Human" entry already tells Jimmy to route order-specific or account-specific questions to the <em>Message Us</em> button. Add to that list any topic you'd rather a human handle.
            </p>
          </div>
        )}
      </div>

      <div className="card" style={{ marginBottom: 20 }}>
        <div className="card-header" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <MessageCircle size={14} /> Greeting Message
          <span style={{ marginLeft: 'auto', fontWeight: 400, fontSize: 11, color: 'var(--gray-500)' }}>
            {greetingSavedAt ? 'Saved just now' : (greetingUpdatedBy ? `Last edited by ${greetingUpdatedBy}` : 'Using default greeting')}
          </span>
        </div>
        <div style={{ padding: 16 }}>
          <p style={{ margin: 0, marginBottom: 10, fontSize: 12.5, color: 'var(--gray-600)' }}>
            This is what Jimmy says the moment a shopper opens the chat. Keep it friendly and short. Press <strong>Enter</strong> to start a new paragraph. Bold and italic are the only formatting options.
          </p>
          <div style={{ display: 'flex', gap: 4, padding: 4, borderBottom: '1px solid var(--gray-200)', background: 'var(--gray-50)', borderRadius: '6px 6px 0 0' }}>
            <button type="button" onMouseDown={e => e.preventDefault()} onClick={() => exec('bold')} title="Bold (Ctrl+B)" style={{ background: 'none', border: 'none', padding: '4px 8px', cursor: 'pointer', borderRadius: 4, color: 'var(--navy)' }}>
              <Bold size={14} />
            </button>
            <button type="button" onMouseDown={e => e.preventDefault()} onClick={() => exec('italic')} title="Italic (Ctrl+I)" style={{ background: 'none', border: 'none', padding: '4px 8px', cursor: 'pointer', borderRadius: 4, color: 'var(--navy)' }}>
              <Italic size={14} />
            </button>
            <span style={{ width: 1, background: 'var(--gray-300)', margin: '4px 4px' }} />
            <button type="button" onMouseDown={e => e.preventDefault()} onClick={resetGreeting} title="Reset to default" style={{ background: 'none', border: 'none', padding: '4px 8px', cursor: 'pointer', borderRadius: 4, color: 'var(--gray-600)', display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: 11 }}>
              <RotateCcw size={12} /> Reset
            </button>
          </div>
          <div
            ref={editorRef}
            contentEditable
            suppressContentEditableWarning
            onInput={onEditorInput}
            onBlur={onEditorInput}
            style={{
              border: '1px solid var(--gray-300)', borderTop: 'none',
              borderRadius: '0 0 6px 6px',
              padding: '12px 14px', minHeight: 100, fontSize: 14, lineHeight: 1.5,
              color: 'var(--navy)', background: '#fff', outline: 'none',
              fontFamily: 'inherit',
            }}
          />
          <div style={{ marginTop: 10, fontSize: 11, color: 'var(--gray-500)' }}>
            <strong>Preview (how the shopper sees it):</strong>
          </div>
          <div style={{ marginTop: 6, padding: '10px 14px', background: '#dbecf8', borderRadius: 14, fontSize: 13.5, lineHeight: 1.45, color: 'var(--navy)', maxWidth: 320 }} dangerouslySetInnerHTML={{ __html: sanitizeIntroHtml(greeting) || '<em style="color:#9ca3af">Empty</em>' }} />
          <div style={{ display: 'flex', gap: 8, marginTop: 12, alignItems: 'center' }}>
            <button onClick={saveGreeting} disabled={greetingSaving || !greetingDirty} style={BTN('var(--orange)', greetingSaving || !greetingDirty)}>
              <Save size={14} /> {greetingSaving ? 'Saving...' : greetingDirty ? 'Save Greeting' : 'Saved'}
            </button>
            {greetingUpdatedAt && !greetingSavedAt && (
              <span style={{ fontSize: 11, color: 'var(--gray-500)' }}>
                Last updated: {new Date(greetingUpdatedAt).toLocaleString()}
              </span>
            )}
          </div>
        </div>
      </div>

      <div className="card" style={{ marginBottom: 20 }}>
        <div className="card-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <span>Entries ({visible.length}{visible.length !== entries.length ? ` of ${entries.length}` : ''})</span>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            <select value={filterCategory} onChange={e => setFilterCategory(e.target.value)} style={{ ...INPUT, width: 'auto', padding: '4px 8px', fontSize: 12 }}>
              <option value="All">All Categories</option>
              {categoriesInUse.map(c => <option key={c} value={c}>{c}</option>)}
            </select>
            <button onClick={() => setShowInactive(s => !s)} style={{ background: 'none', border: 'none', color: 'var(--gray-600)', fontSize: 12, cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 4 }}>
              {showInactive ? <Eye size={14} /> : <EyeOff size={14} />} {showInactive ? 'Hiding inactive' : 'Showing active only'}
            </button>
            <button onClick={startNew} style={BTN('var(--orange)')}><Plus size={14} /> New Entry</button>
          </div>
        </div>
        <div style={{ padding: 0, overflow: 'auto' }}>
          {loading ? (
            <div style={{ padding: 20, textAlign: 'center', color: 'var(--gray-500)' }}>Loading...</div>
          ) : visible.length === 0 ? (
            <div style={{ padding: 32, textAlign: 'center', color: 'var(--gray-500)', fontSize: 13 }}>
              No entries yet. Click <strong>New Entry</strong> to add Jimmy's first fact.
            </div>
          ) : (
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
              <thead>
                <tr style={{ background: 'var(--gray-50)', borderBottom: '1px solid var(--gray-200)' }}>
                  <th style={{ textAlign: 'left', padding: '8px 14px', fontSize: 11, fontWeight: 600, color: 'var(--gray-600)', textTransform: 'uppercase' }}>Category</th>
                  <th style={{ textAlign: 'left', padding: '8px 14px', fontSize: 11, fontWeight: 600, color: 'var(--gray-600)', textTransform: 'uppercase' }}>Title</th>
                  <th style={{ textAlign: 'left', padding: '8px 14px', fontSize: 11, fontWeight: 600, color: 'var(--gray-600)', textTransform: 'uppercase' }}>Updated</th>
                  <th style={{ textAlign: 'center', padding: '8px 14px', fontSize: 11, fontWeight: 600, color: 'var(--gray-600)', textTransform: 'uppercase' }}>Status</th>
                  <th style={{ textAlign: 'right', padding: '8px 14px', fontSize: 11, fontWeight: 600, color: 'var(--gray-600)', textTransform: 'uppercase' }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {visible.map(entry => (
                  <tr key={entry.id} style={{ borderBottom: '1px solid var(--gray-100)', background: entry.is_active ? 'white' : 'var(--gray-50)' }}>
                    <td style={{ padding: '10px 14px', color: 'var(--gray-700)' }}>
                      <span style={{ background: 'var(--gray-100)', padding: '2px 8px', borderRadius: 999, fontSize: 11, fontWeight: 600, color: 'var(--navy)' }}>{entry.category}</span>
                    </td>
                    <td style={{ padding: '10px 14px', fontWeight: 600, color: 'var(--navy)' }}>{entry.title}</td>
                    <td style={{ padding: '10px 14px', color: 'var(--gray-500)', fontSize: 12 }}>
                      {fmtDate(entry.updated_at)}{entry.updated_by_name ? ` · ${entry.updated_by_name}` : ''}
                    </td>
                    <td style={{ padding: '10px 14px', textAlign: 'center' }}>
                      <span style={{ fontSize: 11, fontWeight: 600, color: entry.is_active ? 'var(--green)' : 'var(--gray-500)' }}>
                        {entry.is_active ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                    <td style={{ padding: '10px 14px', textAlign: 'right' }}>
                      <button onClick={() => toggleActive(entry)} title={entry.is_active ? 'Deactivate' : 'Activate'} style={{ background: 'none', border: 'none', color: 'var(--gray-500)', cursor: 'pointer', padding: 4, marginRight: 4 }}>
                        <Power size={15} />
                      </button>
                      <button onClick={() => startEdit(entry)} title="Edit" style={{ background: 'none', border: 'none', color: 'var(--navy)', cursor: 'pointer', padding: 4, marginRight: 4 }}>
                        <Edit2 size={15} />
                      </button>
                      <button onClick={() => remove(entry)} title="Delete" style={{ background: 'none', border: 'none', color: 'var(--red)', cursor: 'pointer', padding: 4 }}>
                        <Trash2 size={15} />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>

      {editing && (
        <div id="jk-editor" className="card" style={{ marginBottom: 40 }}>
          <div className="card-header">{editing.id ? `Edit Entry #${editing.id}` : 'New Entry'}</div>
          <div style={{ padding: 20, display: 'grid', gap: 14 }}>
            <div style={{ display: 'grid', gridTemplateColumns: '220px 1fr 120px', gap: 12 }}>
              <div>
                <label style={{ fontSize: 11, fontWeight: 600, color: 'var(--gray-600)', textTransform: 'uppercase', display: 'block', marginBottom: 4 }}>Category</label>
                <select
                  value={customCategory ? '__custom' : editing.category}
                  onChange={e => {
                    if (e.target.value === '__custom') setCustomCategory(editing.category);
                    else { setEditing({ ...editing, category: e.target.value }); setCustomCategory(''); }
                  }}
                  style={INPUT}
                >
                  {DEFAULT_CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
                  {!DEFAULT_CATEGORIES.includes(editing.category) && editing.category && !customCategory && <option value={editing.category}>{editing.category}</option>}
                  <option value="__custom">+ Custom category...</option>
                </select>
                {customCategory !== '' && (
                  <input
                    type="text"
                    placeholder="New category name"
                    value={customCategory}
                    onChange={e => setCustomCategory(e.target.value)}
                    style={{ ...INPUT, marginTop: 6 }}
                  />
                )}
              </div>
              <div>
                <label style={{ fontSize: 11, fontWeight: 600, color: 'var(--gray-600)', textTransform: 'uppercase', display: 'block', marginBottom: 4 }}>Title</label>
                <input
                  type="text"
                  value={editing.title}
                  onChange={e => setEditing({ ...editing, title: e.target.value })}
                  placeholder="e.g. Blue Crab Grades Explained"
                  style={INPUT}
                  maxLength={255}
                />
              </div>
              <div>
                <label style={{ fontSize: 11, fontWeight: 600, color: 'var(--gray-600)', textTransform: 'uppercase', display: 'block', marginBottom: 4 }}>Sort Order</label>
                <input
                  type="number"
                  value={editing.sort_order}
                  onChange={e => setEditing({ ...editing, sort_order: e.target.value })}
                  style={INPUT}
                />
              </div>
            </div>

            <div>
              <label style={{ fontSize: 11, fontWeight: 600, color: 'var(--gray-600)', textTransform: 'uppercase', display: 'block', marginBottom: 4 }}>
                Content <span style={{ fontWeight: 400, textTransform: 'none', color: 'var(--gray-500)' }}>— plain text or markdown; Jimmy uses this verbatim as background knowledge.</span>
              </label>
              <textarea
                value={editing.content}
                onChange={e => setEditing({ ...editing, content: e.target.value })}
                placeholder="Tell Jimmy what to know. Stick to facts: specifics, exact policies, exact language. Skip fluff."
                style={TEXTAREA}
              />
              <div style={{ fontSize: 11, color: 'var(--gray-500)', marginTop: 4 }}>{editing.content.length.toLocaleString()} characters</div>
            </div>

            <label style={{ display: 'inline-flex', alignItems: 'center', gap: 8, fontSize: 13, color: 'var(--navy)', cursor: 'pointer' }}>
              <input type="checkbox" checked={!!editing.is_active} onChange={e => setEditing({ ...editing, is_active: e.target.checked })} />
              Active — include this entry in Jimmy's knowledge
            </label>

            <div style={{ display: 'flex', gap: 8, paddingTop: 6, borderTop: '1px solid var(--gray-100)' }}>
              <button onClick={save} disabled={saving} style={BTN('var(--orange)', saving)}>
                <Save size={14} /> {saving ? 'Saving...' : 'Save'}
              </button>
              <button onClick={cancel} disabled={saving} style={BTN('var(--gray-500)', saving)}>
                <X size={14} /> Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
